import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import AgentBetaIntroModal from '../components/AgentBetaIntroModal';
import { AGENT_BETA_TERMS_VERSION } from '../lib/agentBetaConstants';

type AgentBetaContextValue = {
  loaded: boolean;
  accepted: boolean;
  /** Open Agents, or the beta intro if this user has not accepted yet. */
  openAgents: (path?: string) => void;
};

const AgentBetaContext = createContext<AgentBetaContextValue | null>(null);

export function useAgentBeta() {
  const value = useContext(AgentBetaContext);
  if (!value) throw new Error('useAgentBeta must be used within AgentBetaProvider');
  return value;
}

function cacheKey(userId: string) {
  return `agent_beta_terms_accepted:${userId}`;
}

export function AgentBetaProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [accepted, setAccepted] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const pendingPath = useRef('/agents');

  useEffect(() => {
    let cancelled = false;
    const userId = user?.id;
    if (!userId || !supabase) {
      setAccepted(false);
      setLoaded(true);
      return;
    }
    setLoaded(false);
    try {
      if (localStorage.getItem(cacheKey(userId)) === String(AGENT_BETA_TERMS_VERSION)) {
        setAccepted(true);
        setLoaded(true);
      }
    } catch { /* ignore */ }

    void supabase
      .from('user_agent_beta_acceptance')
      .select('version')
      .eq('user_id', userId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        const ok = (data?.version ?? 0) >= AGENT_BETA_TERMS_VERSION;
        setAccepted(ok);
        try {
          if (ok) localStorage.setItem(cacheKey(userId), String(AGENT_BETA_TERMS_VERSION));
          else localStorage.removeItem(cacheKey(userId));
        } catch { /* ignore */ }
        setLoaded(true);
      });

    return () => { cancelled = true; };
  }, [user?.id]);

  const openAgents = useCallback((path = '/agents') => {
    pendingPath.current = path;
    if (accepted) {
      navigate(path);
      return;
    }
    setShowIntro(true);
  }, [accepted, navigate]);

  // A click that landed before the acceptance check finished should go straight in.
  useEffect(() => {
    if (!loaded || !accepted || !showIntro) return;
    setShowIntro(false);
    navigate(pendingPath.current);
  }, [loaded, accepted, showIntro, navigate]);

  const handleAccepted = () => {
    setAccepted(true);
    setShowIntro(false);
    try {
      if (user?.id) localStorage.setItem(cacheKey(user.id), String(AGENT_BETA_TERMS_VERSION));
    } catch { /* ignore */ }
    const path = pendingPath.current || '/agents';
    const here = `${location.pathname}${location.search}`;
    if (here !== path) navigate(path);
  };

  const handleClose = () => {
    if (showIntro === false) return;
    setShowIntro(false);
    if (!accepted && location.pathname === '/agents') {
      navigate('/', { replace: true });
    }
  };

  return (
    <AgentBetaContext.Provider value={{ loaded, accepted, openAgents }}>
      {children}
      <AgentBetaIntroModal
        isOpen={showIntro}
        onClose={handleClose}
        onAccepted={handleAccepted}
      />
    </AgentBetaContext.Provider>
  );
}
